import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  FilterSelectorSection,
  InfoBar,
  SmallButton,
} from "@etframework/ui.components";
import { FilterSelectorColumnTypes as types } from "@etframework/ui.components";

import { PropertyUpdateRowInput } from "./PropertyUpdateRowInput";
import useRolePermissionStore from "../../store/rolePermissionStore";
import { useBulkUpdateDataHelper } from "../../hooks/BulkUpdateDataHelper";
import { useTaskTypeByBoardId } from "../../data/queries/taskTypeQueries";
import Enums from "../../types/enums";
import { useBoardCustomFields } from "../../data/queries/boardQueries";

/*
  ColumnSetting :
    id: string              <- Used to differentiate between two options for the same field.
    field: string           <- Actual database name of the property. 
    type: string            <- Type of the data source: date, source(aka query), number, boolean. 
    propertyType: string    <- does it allow multiple values?? if so { propertyType: "array"}. 
    headerText: string      <- Label for the input. Translations are in BulkUpdateModal.UpdateEditor 
*/

export const UpdateEditor = forwardRef(
  (
    { id, value, onChange, columnSettings = [], defaultUpdates = null, board },
    ref
  ) => {
    const { t } = useTranslation(["BulkUpdateTasksModal", "Shared"], {
      useSuspense: false,
    });
    const { data: taskTypes } = useTaskTypeByBoardId(board.Id);
    const { data: customFields } = useBoardCustomFields(board.Id);
    const { getDataSource } = useBulkUpdateDataHelper();

    const inputRefs = useRef({});
    const infoBarRef = useRef(null);
    const lastAdded = useRef(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [updates, setUpdates] = useState(
      value ??
        defaultUpdates?.reduce((p, c) => {
          p[c.field] = { checked: true, settings: c };
          return p;
        }, {}) ??
        {}
    );

    var customFieldValues = [];

    if (customFields) {
      const customFieldsToAdd = customFields
        .filter((x) => !columnSettings.some((col) => col.field === x.Name))
        .map((x) => {
          var type = types.custom;
          switch (x.Type) {
            case Enums.CustomFieldDefinitionType.Date:
              type = types.date;
              break;
            case Enums.CustomFieldDefinitionType.Number:
              type = types.number;
              break;
            case Enums.CustomFieldDefinitionType.Boolean:
              type = types.boolean;
              break;
          }

          return {
            field: x.Name,
            type: type,
            displayProperty: "Value",
            headerText: x.Name,
            noTranslations: true,
            isCustomField: true,
            customField: x,
          };
        });

      columnSettings.push(...customFieldsToAdd);
    }

    const rolePermissions = useRolePermissionStore(
      (state) => state.rolePermissions
    );

    const showTaxReturn = taskTypes.find(
      (x) => x.Type === Enums.TaskType.TaxReturn
    );

    const dropDownItems = useMemo(() => {
      let items = columnSettings.map((settings) => ({
        text: settings.noTranslations
          ? settings.headerText
          : t(`Columns.${settings.headerText}`),
        id: [settings.field, settings.id].join(","),
        field: settings.field,
      }));

      if (!rolePermissions?.CanComplete) {
        items = items.filter((item) => item?.id !== "StageId");
      }

      if (!showTaxReturn) {
        items = items.filter(
          (item) => !item.id.startsWith("TaskTaxReturnInfo")
        );
      }

      return items
        .filter((x) => !updates[x.field]?.checked)
        .sort((l, r) => l.text?.localeCompare?.(r.text) ?? 0);
    }, [updates, showTaxReturn]);

    const remove = (field) => {
      const copy = updates ? { ...updates } : {};
      copy[field].checked = false;
      copy[field].value = null;
      onChange?.({ properties: copy });
      setUpdates(copy);
    };

    const add = (e) => {
      const [field, settingsId] = e.properties.id.split(",");
      const copy = updates ? { ...updates } : {};
      const settings = columnSettings.find(
        (x) => x.field === field && (!x.id || x.id === settingsId)
      );
      copy[field] = {
        checked: true,
        settings: settings,
      };
      lastAdded.current = field;
      onChange?.({ properties: copy });
      setUpdates(copy);
    };

    const _onChange = () => {
      onChange?.({ properties: getValue() });
    };

    const updateList = Object.keys(updates)
      ?.filter(
        (x) =>
          updates[x].checked &&
          (showTaxReturn || !x.startsWith("TaskTaxReturnInfo"))
      )
      .map((field) => ({
        field: field,
        ...updates[field],
      }))
      .sort((l, r) => {
        const lDisplayText = l.settings.noTranslations
          ? l.settings.headerText
          : t(`Columns.${l.settings?.headerText}`);
        const rDisplayText = r.settings.noTranslations
          ? r.settings.headerText
          : t(`Columns.${r.settings?.headerText}`);
        return lDisplayText?.localeCompare?.(rDisplayText) || 0;
      });

    const getAppliedValue = () => {
      var value = {};
      Object.entries(inputRefs.current)
        .filter((x) => !!x[1])
        .forEach(([field, ref]) => {
          if (!ref.applyValue)
            console.error("Missing applyValue function for: " + field);

          const refValue = ref.getValue();

          if (refValue?.settings?.isCustomField) {
            customFieldValues.push({
              CustomFieldId: refValue?.settings?.customField.CustomFieldId,
              Value: refValue?.displayValue ?? refValue?.value,
            });
            value["CustomField"] = customFieldValues;
          } else ref?.applyValue?.(value);
        });
      return value;
    };

    const getValue = () => {
      const value = {};
      Object.entries(inputRefs.current)
        .filter((x) => !!x[1])
        .forEach(([field, ref]) => {
          if (!ref.getValue)
            console.error("Missing getValue function for: " + field);
          else {
            const fieldValue = ref?.getValue?.();
            value[field] = {
              ...fieldValue,
              checked: true,
              settingsId: fieldValue?.settings?.id,
            };
          }
        });
      return value;
    };

    const fields = useMemo(() => Object.keys(updates), [updates]);
    useEffect(() => {
      if (lastAdded.current) {
        inputRefs.current[lastAdded.current]?.focus();
      }
    }, [fields]);

    useImperativeHandle(
      ref,
      () => ({
        getAppliedValue /* What you want to use for the API request*/,
        getValue,
        setError: (e) => {
          setErrorMessage(e);
          infoBarRef.current.open();
        },
        closeError: () => infoBarRef.current.close(),
        showError: () => infoBarRef.current.open(),
      }),
      []
    );

    const addButton = (
      <SmallButton
        className={" w-fit"}
        iconCss={"fa-regular fa-plus"}
        isDropdown={true}
        onClick={add}
        text={t("UpdateEditor.AddButton")}
        dropdownItems={dropDownItems}
      />
    );

    return (
      <FilterSelectorSection
        id={id}
        className="grow py-12px overflow-hidden d-flex flex-column gap-2"
      >
        <div className="px-3">
          <InfoBar
            id="info-bar"
            ref={infoBarRef}
            opened={false}
            message={errorMessage}
            variant={"error"}
            iconCss={"fa-regular fa-triangle-exclamation"}
          />
        </div>
        <div
          className="overflow-auto d-flex flex-column gap-2 px-3"
          tabIndex={-1}
        >
          {updateList &&
            updateList?.length != 0 &&
            updateList.map(({ field, settings, action }) => {
              return (
                <PropertyUpdateRowInput
                  ref={(ref) => (inputRefs.current[field] = ref)}
                  key={`${id}_${field}-input`}
                  id={`${id}_${field}-input`}
                  field={field}
                  onRemove={remove}
                  value={updates[field].value}
                  settings={settings}
                  action={action}
                  onChange={_onChange}
                  getDataSource={getDataSource}
                  properties={updates}
                />
              );
            })}
          {dropDownItems.length > 0 && addButton}
        </div>
      </FilterSelectorSection>
    );
  }
);
